import { type MenuOptionType, useContextMenuMESF } from "@dexteel/mesf-core";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import { Box, Button, Tooltip, Typography, useMediaQuery } from "@mui/material";
import type {
	ColDef,
	GetRowIdParams,
	ICellRendererParams,
	RowClassParams,
	RowStyle,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { type MouseEvent, useEffect, useMemo } from "react";
import { IconChip } from "@/pages/sawlines/components/primitives";
import { shownTagNo } from "@/pages/sawlines/utils/entryBarcode";
import type { MillBundle } from "../repositories/tagStationRepository";
import { changed, fmtLen, official, printing, sent } from "../tagStation";
import { printState, STATE } from "./BundleCard";
import { Card, Count } from "./Card";

export interface RowHandlers {
	onSelect: (bundle: MillBundle) => void;
	onPrint: (bundle: MillBundle) => void;
	onScrap: (bundle: MillBundle) => void;
	onDelete: (bundle: MillBundle) => void;
}

const MENU = "tagStationContext";

// selected rides on the row so the grid restyles when it changes
type Row = MillBundle & { selected: boolean };

const corrected = (b: MillBundle) =>
	b.State === "Printed" && changed(sent(b), official(b)).length > 0;

// 24px each: four fit the column, chips still show at 1920
const actionSx = {
	minWidth: 24,
	padding: "2px",
	"& svg": { fontSize: 20 },
} as const;

type RowAction = Omit<MenuOptionType, "onClick"> & {
	onClick: () => void;
	sx?: object;
	// tooltip when disabled
	why?: string;
};

// row icons and right-click menu: same actions, same words; color only on the row buttons
const actionsOf = (
	b: MillBundle,
	h: RowHandlers,
): Record<"open" | "print" | "scrap" | "delete", RowAction> => {
	const printed = b.State === "Printed";
	return {
		open: {
			key: "open",
			name: printed ? "Open it" : "Check and correct it",
			icon: <EditIcon />,
			onClick: () => h.onSelect(b),
		},
		print: {
			key: "print",
			name: printed ? "Reprint the tag" : "Print the tag",
			icon: <PrintIcon />,
			onClick: () => h.onPrint(b),
			disabled: printing(b),
			why: "Already waiting to print",
			sx: { color: "#1565C0" },
		},
		scrap: {
			key: "scrap",
			name: b.IsScrap ? "Not scrap" : "Mark as scrap",
			icon: <BlockIcon />,
			onClick: () => h.onScrap(b),
			sx: b.IsScrap
				? {
						color: "#fff",
						bgcolor: "#c62828",
						"&:hover": { bgcolor: "#b71c1c" },
					}
				: { color: "rgba(0,0,0,.45)" },
		},
		delete: {
			key: "delete",
			name: "Delete it",
			icon: <DeleteIcon />,
			onClick: () => h.onDelete(b),
			disabled: !b.IsManual,
			why: "Only bundles added by hand can be deleted",
			sx: { color: "error.main" },
		},
	};
};
const gridFrame = {
	height: "100%",
	border: "1px solid #ccc",
	borderRadius: 4,
} as const;

const getRowId = (params: GetRowIdParams<Row>) =>
	String(params.data.MillBundleId);

const getRowStyle = ({ data }: RowClassParams<Row>): RowStyle | undefined => {
	if (!data) return undefined;
	if (data.selected)
		return { background: "#e8f5e9", boxShadow: "inset 4px 0 0 #2E7D32" };
	if (data.IsScrap) return { background: "#FBDFE1" };
	if (data.State === "Aside" || corrected(data))
		return { background: "#FCF3D6" };
	return data.State === "Printed" && !printState(data)
		? { color: "rgba(0,0,0,.6)" }
		: undefined;
};

const useColumns = (handlers: RowHandlers) => {
	// a third of the screen per table: chips fit from 1900px, pieces below it
	const wide = useMediaQuery("(min-width:1900px)");
	const { onSelect, onPrint, onScrap, onDelete } = handlers;
	return useMemo(
		(): ColDef<Row>[] => [
			{
				field: "TagNo",
				headerName: "Tag",
				flex: 1.3,
				minWidth: 88,
				valueFormatter: (p) => (p.value ? shownTagNo(p.value) : "—"),
				cellRenderer: ({ data, valueFormatted }: ICellRendererParams<Row>) => {
					const s = data && printState(data);
					return s ? (
						<Tooltip title={s.title}>
							<Box
								component="span"
								sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
							>
								<s.Icon sx={{ fontSize: 18 }} />
								{valueFormatted}
							</Box>
						</Tooltip>
					) : (
						valueFormatted
					);
				},
				cellStyle: (p) => ({
					fontWeight: 700,
					textAlign: "center",
					color: (p.data && printState(p.data)?.color) || "inherit",
				}),
			},
			{ field: "SectionName", headerName: "Section", flex: 1, minWidth: 70 },
			{
				field: "GradeName",
				headerName: "Gr",
				flex: 0.7,
				minWidth: 42,
				valueFormatter: (p) => p.value ?? "—",
			},
			{
				field: "LengthIn",
				headerName: "Length",
				flex: 1.1,
				minWidth: 69,
				valueFormatter: (p) => fmtLen(p.value),
			},
			{
				field: "Pieces",
				headerName: "Pcs",
				flex: 0.8,
				minWidth: 48,
				hide: wide,
			},
			{
				colId: "status",
				headerName: "Status",
				flex: 3,
				minWidth: 222,
				hide: !wide,
				sortable: false,
				cellStyle: {
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					gap: 4,
				},
				cellRenderer: ({ data }: ICellRendererParams<Row>) => {
					if (!data) return null;
					const s = printState(data);
					return (
						<Box
							sx={{
								display: "contents",
								"& > span": {
									width: 108,
									justifyContent: "center",
									lineHeight: 1.5,
								},
							}}
						>
							{data.State === "Station" && (
								<IconChip
									label={STATE.Station.label}
									color={STATE.Station.color}
									filled
								/>
							)}
							{data.State === "Aside" && (
								<IconChip
									label={STATE.Aside.label}
									color={STATE.Aside.color}
									Icon={AccessTimeIcon}
								/>
							)}
							{s && <IconChip {...s} />}
							{data.IsScrap && (
								<IconChip label="Scrap" color="#c62828" Icon={BlockIcon} />
							)}
							{corrected(data) && (
								<IconChip label="Corrected" color="#8a5a00" />
							)}
						</Box>
					);
				},
			},
			{
				colId: "actions",
				headerName: "",
				minWidth: 100,
				maxWidth: 100,
				sortable: false,
				cellStyle: { padding: 0 },
				cellRenderer: ({ data }: ICellRendererParams<Row>) => {
					if (!data) return null;
					const a = actionsOf(data, { onSelect, onPrint, onScrap, onDelete });
					return (
						<Box sx={{ display: "flex", justifyContent: "center" }}>
							{[a.print, a.open, a.scrap, a.delete].map((action) => (
								<Tooltip
									key={action.key}
									title={action.disabled ? action.why : action.name}
								>
									{/* span: a disabled button has no tooltip */}
									<span>
										<Button
											size="small"
											aria-label={action.name}
											aria-pressed={
												action === a.scrap ? data.IsScrap : undefined
											}
											disabled={action.disabled}
											onClick={action.onClick}
											sx={{ ...actionSx, ...action.sx }}
										>
											{action.icon}
										</Button>
									</span>
								</Tooltip>
							))}
						</Box>
					);
				},
			},
		],
		[wide, onSelect, onPrint, onScrap, onDelete],
	);
};

export const BundleTables = ({
	bundles,
	currentId,
	onNew,
	...handlers
}: RowHandlers & {
	bundles: MillBundle[];
	currentId: number | null;
	onNew: () => void;
}) => {
	const columnDefs = useColumns(handlers);
	const { onSelect } = handlers;
	const { showContextMenu, registerConfig } = useContextMenuMESF();
	// handlers never change: registered once
	useEffect(() => {
		registerConfig({
			id: MENU,
			getOptions: (bundle: MillBundle) => {
				const a = actionsOf(bundle, handlers);
				return [
					// bold: what a click on the row does
					{ ...a.open, style: { fontWeight: 700 } },
					a.print,
					a.scrap,
					a.delete,
					{ key: "new", name: "New bundle", icon: <AddIcon />, onClick: onNew },
				];
			},
		});
	}, []);
	const rows = useMemo(
		() =>
			bundles.map(
				(b): Row => ({ ...b, selected: b.MillBundleId === currentId }),
			),
		[bundles, currentId],
	);
	// the one at the station first, then newest first; a tag printing or not sent stays here
	const toPrint = rows
		.filter((r) => r.State !== "Printed" || printState(r))
		.sort(
			(a, b) => Number(b.State === "Station") - Number(a.State === "Station"),
		);
	const printed = rows
		.filter((r) => r.State === "Printed" && !printState(r))
		.sort((a, b) => (b.PrintedAt ?? "").localeCompare(a.PrintedAt ?? ""));

	const grid = (rowData: Row[], empty: string) => (
		<Box
			sx={{
				m: 1,
				flex: 1,
				minHeight: 0,
				overflow: "hidden",
				"& .ag-row": { cursor: "pointer" },
			}}
		>
			<div style={gridFrame} onContextMenu={(e) => e.preventDefault()}>
				<AgGridReact<Row>
					rowData={rowData}
					columnDefs={columnDefs}
					suppressContextMenu
					suppressCellFocus
					headerHeight={35}
					rowHeight={38}
					getRowId={getRowId}
					getRowStyle={getRowStyle}
					// buttons in a row act on their own
					onCellContextMenu={(e) =>
						e.event &&
						showContextMenu(e.event as unknown as MouseEvent, e.data, MENU)
					}
					onRowClicked={(e) =>
						e.data &&
						!(e.event?.target as HTMLElement | null)?.closest("button") &&
						onSelect(e.data)
					}
					overlayNoRowsTemplate={`<span style="color:rgba(0,0,0,.6)">${empty}</span>`}
				/>
			</div>
		</Box>
	);

	return (
		<>
			<Card title="On the way" sub="coming down the transfer">
				<Typography
					color="text.secondary"
					sx={{ px: 2, py: 1.25, fontSize: 14 }}
				>
					No sensors yet. Add each bundle with New bundle when it arrives.
				</Typography>
			</Card>
			<Card
				title="To print"
				badge={<Count>{toPrint.length}</Count>}
				action={
					<Button variant="contained" startIcon={<AddIcon />} onClick={onNew}>
						New bundle
					</Button>
				}
			>
				{grid(toPrint, "Nothing waiting for a tag.")}
			</Card>
			<Card
				title="Printed this shift"
				badge={<Count gray>{printed.length}</Count>}
			>
				{grid(printed, "Nothing printed yet this shift.")}
			</Card>
		</>
	);
};
